import { odata } from '@bingads-webui/http-util';
import { INotification, TargetComponent, NotificationLevel, DismissMode, ScopeLevel, IContext } from '../../types';
import { isBillingLinkPermission } from '../../utils';

export interface IStores{
  '@odata.context': string,
  ProviderIds: number[],
}

export class GMCTokenExpiredNotification {
  context: IContext;
  i18n: any;
  availableScopes: ScopeLevel[];

  constructor(context: IContext) {
    this.context = context;
    this.i18n = context.getNewI18n();
    this.availableScopes = [ScopeLevel.account, ScopeLevel.customer];
  }

  async getAlerts(): Promise<(INotification | null)[]> {
    if (!this.context.getUserContext().CurrentUser.IsInternal && isBillingLinkPermission(this.context.getSettingsContext())) {
      return [];
    }

    const permissions = this.context.getCurrentPermissions();
    const isMMCEnabled = !permissions.IsDomesticSimplifiedChineseEnabled && permissions.IsEnhancedShoppingCampaignAllowed;
    const isGMCTokenAlertEnabled = permissions.dynamic.GMCTokenAlert;
    if (!isMMCEnabled || !isGMCTokenAlertEnabled) {
      return [];
    }

    const storeIds = await this.getIssuedStores();
    if (!storeIds || storeIds.length === 0) {
      return [];
    }

    const message = storeIds.length === 1 ? this.i18n.getString(_TL_('Your Google Merchant Center store imports are interrupted. To resume your imports, you’ll need to revalidate your authentication. Navigate to the <b>Import</b> tab in <b>Merchant Center</b> or select <b>Go to Merchant Center Import</b> below, where you’ll be prompted to sign in again.')) :
      this.i18n.getString(_TL_('{{number}} of your Google Merchant Center store imports are interrupted. To resume your imports, you’ll need to revalidate your authentication. Navigate to the <b>Import</b> tab in <b>Merchant Center</b> or select <b>Go to Merchant Center Import</b> below, where you’ll be prompted to sign in again.'), { number: storeIds.length });

    // unified queue notification
    const notification: INotification = {
      id: `GMCTokenExpiredNotification-${storeIds[0]}`,
      condition: true,
      target: TargetComponent.UnifiedQueue,
      dismissMode: DismissMode.session,
      severity: NotificationLevel.Warning,
      scopeLevel: ScopeLevel.account,
      availableScopes: this.availableScopes,
      title: this.i18n.getString(_TL_('Revalidate your Google Merchant Center')),
      message,
      actionList: [{
        text: this.i18n.getString(_TL_('Go to Merchant Center Import')),
        action: () => {
          this.context.route.goMerchantCenterGMCImortPage(storeIds[0]);
        },
        callbackType: 'closeQueue',
        extraClassNames: ['gmc-token-expired-redirect-button'],
      }],
    };

    // top banner notification
    const topBanner: INotification = {
      id: 'GMCTokenExpiredNotification-TopBanner',
      condition: true,
      target: TargetComponent.TopBanner,
      dismissMode: DismissMode.never,
      severity: NotificationLevel.Error,
      scopeLevel: ScopeLevel.root,
      availableScopes: this.availableScopes,
      title: '',
      message: this.i18n.getString(_TL_('Your Google Merchant Center store imports are interrupted. To resume your imports, navigate to the <b>Import</b> tab in <b>Merchant Center</b> to sign in again.')),
      actionList: [],
    };

    return [notification, topBanner];
  }

  async getIssuedStores() {
    const { aid, cid, uid } = this.context.getCurrentScope().values;
    const url = `/ODataApi/Mmc/v3/Users(${uid})/Customers(${cid})/Accounts(${aid})/Imports/GetGMCTokenExpiredStores?origin=1`;
    const response = await odata.get({
      url,
      oDataToken: this.context.getAuthToken(),
    }) as IStores;

    return response && response.ProviderIds ? response.ProviderIds : [];
  }
}
